import React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { ApiClientScope, ApiScopeGroup } from '../services/apiClientService';

/**
 * Tri-state tree-render of the partner scope catalog.
 *
 * Used by both CreateApiClientModal and EditApiClientScopesModal so the two
 * stay in sync. The parent component owns the `checked` set and the catalog
 * tree (fetched via listApiScopes()); this component just renders + emits
 * updates. Parent root toggles all children; partial selection shows
 * indeterminate.
 */
interface Props {
  tree: ApiScopeGroup[];
  checked: Set<ApiClientScope>;
  onChange: (next: Set<ApiClientScope>) => void;
}

export default function ScopeTreeCheckboxes({ tree, checked, onChange }: Props) {
  return (
    <>
      {tree.map((grp) => {
        const childScopes = grp.children.map((c) => c.scope);
        const checkedChildren = childScopes.filter((s) => checked.has(s));
        const allChecked = checkedChildren.length === childScopes.length;
        const someChecked = checkedChildren.length > 0 && !allChecked;
        const toggleParent = (on: boolean) => {
          const next = new Set(checked);
          childScopes.forEach((s) => (on ? next.add(s) : next.delete(s)));
          onChange(next);
        };
        const toggleChild = (s: ApiClientScope, on: boolean) => {
          const next = new Set(checked);
          on ? next.add(s) : next.delete(s);
          onChange(next);
        };
        return (
          <Box key={grp.app} sx={{ mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={(_, v) => toggleParent(v)}
                />
              }
              label={<strong>{grp.label}</strong>}
            />
            <Box sx={{ pl: 4 }}>
              {grp.children.map((leaf) => (
                <FormControlLabel
                  key={leaf.scope}
                  control={
                    <Checkbox
                      checked={checked.has(leaf.scope)}
                      onChange={(_, v) => toggleChild(leaf.scope, v)}
                    />
                  }
                  label={leaf.label}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </>
  );
}
